import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, Text, useTheme } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import AsyncStorage from '@react-native-async-storage/async-storage';
import { advanceOnboardingState, apiFetch } from '../../api/apiService';
import { API_ENDPOINTS } from '../../api/config';
import { track, resolveEnvironment, makeSemanticEventId } from '../../lib/analytics';
import { AnalyticsEvent } from '../../lib/analyticsEvents';
import { useUser } from '@/src/hooks/useUser';
import { usePaywallConfig } from '@/src/hooks/usePaywallConfig';
import ChatConversation from '@/src/components/chat/ChatConversation';
import ChatBubble from '@/src/components/chat/ChatBubble';
import {
  useProductPricingSurface,
  type ProductPricingSelection,
} from '@/src/components/onboarding/useProductPricingSurface';
import { useProductCatalog } from '@/src/hooks/useProductCatalog';
import { suggestedLicensesForRange, isFleetSizeRange, FLEET_SIZE_RANGES, type FleetSizeRange } from '@/src/features/onboarding/fleetSize';

// Stable JOURNEY INSTANCE key (not user+flow alone).
//
// BACKGROUND (#389 blocker 2 fix): deriving the semantic event id ONLY from
// `userId+flow` suppressed every future same-user journey — the same owner
// re-entering onboarding (after completion / a fresh start) would collapse to
// the ORIGINAL emission and never report the new journey. We instead persist an
// explicit `journeyKey` that:
//   - stays STABLE across remount / React StrictMode double-mount of the SAME
//     logical journey (so the same journey still emits exactly one event), and
//   - is ROTATED / cleared at a real journey boundary (onboarding completion,
//     or a brand-new onboarding start elsewhere) so a subsequent same-user
//     journey emits a DISTINCT, second event.
//
// The key is persisted in AsyncStorage (and web localStorage) so a process-level
// remount — or a real app reload mid-journey — reloads the SAME key and the
// analytics event_id dedupe (makeSemanticEventId + dedupeEventId) collapses the
// re-emission to exactly ONE provider dispatch. We deliberately do NOT keep a
// private module-level "already emitted" Set as the source of truth: the dedupe
// authority is the analytics layer's stable eventId, which survives remount and
// is independent of any in-memory guard that tests could clear. Production
// exactly-once is therefore enforced by the event id, not by a fragile guard.
const JOURNEY_KEY_STORAGE = 'owner_pricing_journey_instance';
let journeyKey: string | null = null;

const loadJourneyKey = async (): Promise<string> => {
  if (journeyKey) return journeyKey;
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
      const existing = window.localStorage.getItem(JOURNEY_KEY_STORAGE);
      if (existing) {
        journeyKey = existing;
        return journeyKey;
      }
    }
    const stored = await AsyncStorage.getItem(JOURNEY_KEY_STORAGE);
    if (stored) {
      journeyKey = stored;
      return journeyKey;
    }
  } catch {
    // fall through to a freshly minted key
  }
  journeyKey = `j-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  // Best-effort persist so a remount reloads the same key.
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(JOURNEY_KEY_STORAGE, journeyKey);
    }
    await AsyncStorage.setItem(JOURNEY_KEY_STORAGE, journeyKey);
  } catch {
    // non-fatal
  }
  return journeyKey;
};

/**
 * Rotate the journey key at a real journey boundary so the NEXT owner pricing
 * journey (after completion or a fresh start) emits a DISTINCT event. Called by
 * the plan-commit handler (confirmSelection) below — that is the actual owner
 * journey boundary, not the pricing_viewed emission itself.
 */
export const rotateOwnerPricingJourneyKey = (): void => {
  journeyKey = `j-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(JOURNEY_KEY_STORAGE, journeyKey);
    }
    void AsyncStorage.setItem(JOURNEY_KEY_STORAGE, journeyKey);
  } catch {
    // non-fatal
  }
};

/**
 * TEST-ONLY seam: drop the in-memory journey-key cache to simulate a process
 * reload (a real app restart mid-journey). The next `loadJourneyKey` must then
 * RELOAD the persisted key from storage — proving persistence is the source of
 * truth across reloads, not the in-memory cache. This is deliberately NOT an
 * emission/dedupe guard: it does not suppress any event, only clears the
 * optimization cache so a remount is forced to read storage.
 */
export const __resetJourneyKeyCacheForTests = (): void => {
  journeyKey = null;
};

/**
 * Emit `pricing_viewed` for the current (stable) journey instance.
 *
 * No private in-memory guard here: the eventId embeds the persisted journey key
 * plus the owner id + flow, so the analytics layer's event_id dedupe is the
 * single source of truth for exactly-once. A remount reloads the SAME persisted
 * key -> same eventId -> one provider dispatch; a post-boundary remount (after
 * rotateOwnerPricingJourneyKey) gets a NEW key -> a second, distinct dispatch.
 */
const emitPricingViewedOnce = async (userId?: string | number | null) => {
  const stableKey = await loadJourneyKey();
  const eventId = makeSemanticEventId(AnalyticsEvent.PricingViewed, {
    userId: userId == null ? undefined : String(userId),
    flow: 'owner_pricing',
    stableKey,
  });
  track(
    AnalyticsEvent.PricingViewed,
    { user_role: 'owner', feature: 'hours', billing: 'monthly', app_variant: resolveEnvironment() },
    { eventId },
  );
};

export interface OwnerOnboardingChat {
  bubbles: ReactNode;
  composer: ReactNode;
  selectFleetSize: (range: FleetSizeRange) => Promise<void>;
}

export function useOwnerOnboardingChat(): OwnerOnboardingChat {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const { userData, fetchUserData } = useUser();
  const ownerPersona = userData?.company?.is_individual ? 'OWN-IND' : 'OWN-ENT';
  const recordOwnerProgress = async (
    currentStep: string,
    evidence?: Record<string, unknown>,
  ): Promise<boolean> => {
    try {
      if (typeof advanceOnboardingState === 'function') {
        const response = await advanceOnboardingState(ownerPersona, currentStep, evidence);
        return response.ok;
      }
    } catch {
      // The server-derived state remains authoritative if persistence fails.
    }
    return false;
  };
  const [range, setRange] = useState<FleetSizeRange | null>(
    isFleetSizeRange(userData?.fleet_size_range) ? userData.fleet_size_range : null,
  );
  const [selectionVisible, setSelectionVisible] = useState(false);
  // Stable guard: pricing_viewed must fire EXACTLY ONCE, when the resolved
  // pricing is actually visible to the owner — not on confirmSelection. Dedupe
  // is enforced by the module-level guard in emitPricingViewedOnce, which keys
  // on a deterministic semantic id and survives StrictMode/remount. This ref
  // only short-circuits the effect body for the same mounted instance.
  const pricingViewedRef = useRef(false);
  const [savingRange, setSavingRange] = useState<FleetSizeRange | null>(null);
  const [error, setError] = useState<string | null>(null);
  // #448 — owner registration-only. The owner may decline the mandatory
  // commercial selection here (validated identity is already complete: this
  // surface only renders after the fleet-size answer). This writes the ONE
  // server-authoritative completion and leaves; it never buys, trials or
  // reserves anything. `registrationOnlySubmitRef` is the synchronous
  // double-submit guard (a state flag alone admits both presses in one tick).
  const [leavingForRegistrationOnly, setLeavingForRegistrationOnly] = useState(false);
  const [registrationOnlyError, setRegistrationOnlyError] = useState<string | null>(null);
  const registrationOnlySubmitRef = useRef(false);
  const { config, isLoading: configLoading, error: configError } = usePaywallConfig('owner', 'hours');
  const {
    catalog,
    isLoading: catalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
  } = useProductCatalog();

  const selectFleetSize = async (selected: FleetSizeRange) => {
    setSavingRange(selected);
    setError(null);
    try {
      const response = await apiFetch(API_ENDPOINTS.USER_DETAILS, {
        method: 'PATCH',
        body: JSON.stringify({ fleet_size_range: selected }),
      });
      if (!response.ok) throw new Error('fleet_size_save_failed');
      await recordOwnerProgress('product_phase1');
      setRange(selected);
      setSelectionVisible(true);
      track('onboarding_completed', {
        fleet_size_range: selected,
        user_role: 'owner',
        flow: 'owner_onboarding',
        app_variant: resolveEnvironment(),
      });
    } catch {
      setError(t('fleetSizeSaveError'));
    } finally {
      setSavingRange(null);
    }
  };

  /**
   * Complete owner onboarding WITHOUT a purchase/trial.
   *
   * The server owns the outcome: it accepts `registration_only` only from the
   * `product_phase1` floor (validated company/account identity) and derives the
   * completion back on every later read, so it survives reload and a later
   * login. A failed write keeps the owner on this screen with a retryable error
   * — the client never fabricates the completion from local state.
   */
  const continueWithRegistrationOnly = async () => {
    if (registrationOnlySubmitRef.current) return;
    registrationOnlySubmitRef.current = true;
    setLeavingForRegistrationOnly(true);
    setRegistrationOnlyError(null);

    const recorded = await recordOwnerProgress('registration_only');
    if (!recorded) {
      registrationOnlySubmitRef.current = false;
      setLeavingForRegistrationOnly(false);
      setRegistrationOnlyError(t('registrationOnlyError'));
      return;
    }
    try {
      await fetchUserData?.();
    } catch {
      // The persisted journey is authoritative; the refresh is best effort.
    }
    router.replace('/dashboard/home');
  };

  useEffect(() => {
    if (pricingViewedRef.current) return;
    if (!selectionVisible) return;
    // The pricing surface is now actually visible with resolved config. Emit
    // exactly once. Abandonment is measurable: an owner who leaves before this
    // guard trips never produces pricing_viewed. Dedupe against a deterministic
    // semantic id (owner user id + flow + STABLE JOURNEY INSTANCE key) so a
    // StrictMode double-mount / remount of the SAME journey collapses to ONE
    // emission, while a fresh journey (after rotateOwnerPricingJourneyKey) emits
    // a DISTINCT event.
    pricingViewedRef.current = true;
    void emitPricingViewedOnce(userData?.id);
  }, [selectionVisible, userData?.id]);

  const confirmSelection = async ({
    feature,
    billing,
    plan,
    quantity,
  }: ProductPricingSelection) => {
    // This is the only commit from the mutable pricing surface. Registration
    // has already completed; changing cards/cycles/quantity cannot re-submit it.
    // pricing_viewed is emitted once when the surface becomes visible (the
    // effect above), NOT here — confirmSelection is the plan commit.
    // Real journey boundary: rotate the stable journey instance key so a
    // SAME-USER re-entry into onboarding (after completion / a fresh start)
    // emits a DISTINCT pricing_viewed event, not a dedupe of the original one.
    rotateOwnerPricingJourneyKey();
    track('plan_selected', {
      feature,
      billing,
      price_id: plan.price_id,
      quantity,
      user_role: 'owner',
      app_variant: resolveEnvironment(),
    });
    const recorded = await recordOwnerProgress('config_phase2', {
      selection: {
        feature,
        billing,
        price_id: plan.price_id,
        quantity,
      },
    });
    if (!recorded) {
      setError(t('networkError'));
      return;
    }
    router.replace({
      pathname: '/onboarding/owner',
      params: {
        licenses: String(quantity),
        feature,
        billing,
        price_id: plan.price_id,
      },
    });
  };

  const pricing = useProductPricingSurface({
    catalog,
    catalogLoading,
    catalogError,
    onRetryCatalog: () => void refetchCatalog(),
    paywallConfig: config,
    paywallConfigLoading: configLoading,
    paywallConfigError: configError,
    persona: 'owner',
    initialQuantity: suggestedLicensesForRange(range),
    onConfirm: confirmSelection,
    confirmTestID: 'owner-pricing-confirm',
  });

  // #291 scroll contract: the phase content is part of the conversation, so it
  // sits in the ScrollView. Only the running total and the CTA are pinned — the
  // composer slot is a one-row footer and the catalogue never fitted in it.
  const composer = selectionVisible ? (
    pricing.footer
  ) : (
    <View style={styles.options} accessibilityRole="radiogroup">
      {FLEET_SIZE_RANGES.map(value => (
        <Button
          key={value}
          mode={range === value ? 'contained' : 'outlined'}
          onPress={() => void selectFleetSize(value)}
          loading={savingRange === value}
          disabled={savingRange !== null}
          style={styles.button}
          contentStyle={styles.buttonContent}
          testID={`fleet-size-option-${value}`}
        >
          {t('fleetSizeOptionDrivers', { range: value })}
        </Button>
      ))}
      {error && (
        <Text style={[styles.error, { color: theme.colors.error }]} accessibilityRole="alert">
          {error}
        </Text>
      )}
    </View>
  );

  const bubbles = (
    <>
      <ChatBubble role="assistant" accessibilityRole="header">
        {t('fleetSizeQuestion')}
      </ChatBubble>
      <ChatBubble role="assistant" hint={t('fleetSizeHint')}>
        {' '}
      </ChatBubble>
      {range && (
        <ChatBubble role="user" testID="answer-fleet-size">
          {t('fleetSizeOptionDrivers', { range })}
        </ChatBubble>
      )}
      {selectionVisible && (
        <>
          <ChatBubble
            role="assistant"
            accessibilityRole="header"
            hint={t(pricing.phase === 'product' ? 'productHint' : 'planHint')}
            testID="pricing-surface-question"
          >
            {t(pricing.phase === 'product' ? 'productQuestion' : 'planQuestion')}
          </ChatBubble>
          {/* Phase 2 keeps the phase-1 answer above it as chat history. */}
          {pricing.phase === 'configure' && (
            <ChatBubble role="user" testID="answer-product">
              {t(`productOption_${pricing.selectedFeature}`)}
            </ChatBubble>
          )}
          <View style={styles.pricingSurface} testID="pricing-surface">
            {pricing.content}
          </View>
          {/* #448 — the registration-only escape stays part of the scrollable
              conversation (never the pinned one-row footer), and it renders
              whatever the commercial catalog does: a failing/empty catalog
              must not turn this into a mandatory purchase. */}
          <View style={styles.registrationOnly}>
            <Button
              mode="outlined"
              onPress={() => void continueWithRegistrationOnly()}
              loading={leavingForRegistrationOnly}
              disabled={leavingForRegistrationOnly}
              style={styles.button}
              contentStyle={styles.buttonContent}
              testID="owner-registration-only-cta"
            >
              {t('registrationOnlyCta')}
            </Button>
            <Text
              variant="bodySmall"
              style={[styles.registrationOnlyHint, { color: theme.colors.onSurfaceVariant }]}
            >
              {t('registrationOnlyHint')}
            </Text>
            {registrationOnlyError && (
              <Text
                style={[styles.error, { color: theme.colors.error }]}
                accessibilityRole="alert"
                testID="owner-registration-only-error"
              >
                {registrationOnlyError}
              </Text>
            )}
          </View>
        </>
      )}
    </>
  );

  return { bubbles, composer, selectFleetSize };
}

export function OwnerOnboardingChatView({
  leading,
  testID,
}: {
  leading?: ReactNode;
  testID?: string;
}) {
  const { bubbles, composer } = useOwnerOnboardingChat();
  return (
    <ChatConversation composer={composer} testID={testID}>
      {leading}
      {bubbles}
    </ChatConversation>
  );
}

const styles = StyleSheet.create({
  pricingSurface: {
    marginTop: 4,
    width: '100%',
  },
  registrationOnly: {
    marginTop: 12,
    gap: 6,
    width: '100%',
  },
  registrationOnlyHint: {
    textAlign: 'center',
  },
  options: {
    gap: 12,
  },
  button: {
    borderRadius: 8,
  },
  buttonContent: {
    minHeight: 44,
    paddingVertical: 8,
  },
  error: {
    marginBottom: 12,
    textAlign: 'center',
  },
});

export default useOwnerOnboardingChat;
